import React, { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Avatar, Skeleton, Chip, Stack,
  Pagination, PaginationItem, useTheme,
  List, ListItemButton, ListItemAvatar, ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import NotStartedOutlinedIcon from '@mui/icons-material/NotStartedOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PersonIcon from '@mui/icons-material/Person';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { Helmet } from 'react-helmet-async';
import { getGameActivities, getGameUsers, getGameDetails } from '../services/api';
import { formatActivityTime } from '../utils/dateUtils';
import { formatReviewText } from '../utils/formatText';
import ActivityReactions from '../components/common/ActivityReactions';
import { SITE_FULL_NAME } from '../constants/site';

type TabType = 'activity' | 'members';

const useStatusConfig = () => {
  const theme = useTheme();
  return {
    1: { label: 'プレイ済み', color: theme.palette.primary.main,   Icon: CheckCircleOutlineIcon },
    2: { label: 'プレイ中',   color: theme.palette.info.main,      Icon: PlayCircleOutlineIcon },
    3: { label: '中断',       color: theme.palette.warning.main,   Icon: PauseCircleOutlineIcon },
    4: { label: '積みゲー',   color: '#b99a71',                    Icon: NotStartedOutlinedIcon },
    5: { label: 'ウィッシュ', color: theme.palette.secondary.main, Icon: StarBorderIcon },
  } as const;
};

const getActionText = (actionType: number, oldStatus?: number | null, newStatus?: number | null): string | null => {
  const names: Record<number, string> = { 1: 'プレイ済み', 2: 'プレイ中', 3: '中断', 4: '積みゲー', 5: 'ウィッシュ' };
  if (actionType === 1) {
    switch (newStatus) {
      case 1: return 'プレイ済みに追加';
      case 2: return 'プレイを開始';
      case 3: return '中断に追加';
      case 4: return '積みゲーに追加';
      case 5: return 'ウィッシュリストに追加';
      default: return null;
    }
  }
  if (actionType === 2) {
    if (oldStatus === 1 && newStatus === 2) return 'プレイ済みタイトルのプレイを再開';
    if (oldStatus === 2 && newStatus === 1) return 'プレイを終えた';
    if (oldStatus === 2 && newStatus === 3) return 'プレイを中断';
    if (oldStatus === 3 && newStatus === 2) return '中断していたタイトルのプレイを再開';
    if (oldStatus === 4 && newStatus === 1) return '積みゲーを消化';
    if (oldStatus === 4 && newStatus === 2) return '積みゲーのプレイを開始';
    if (oldStatus === 5 && newStatus === 2) return 'ウィッシュリストのゲームを開始';
    if (oldStatus === 5 && newStatus === 4) return 'ウィッシュリストのゲームを積んだ';
    if (newStatus === 5) return 'ウィッシュリストに追加';
    return `${names[oldStatus ?? 0] ?? ''}から${names[newStatus ?? 0] ?? ''}に変更`;
  }
  if (actionType === 3) return 'レビューを投稿';
  return null;
};

interface ActivityEntry {
  id: number;
  action_type: number;
  user: { user_id: number; username: string; display_name: string; avatar_url?: string };
  old_status?: number | null;
  new_status?: number | null;
  score?: number | null;
  review_text?: string | null;
  reactions: Record<number, number>;
  user_reaction: number | null;
  created_at: string;
}

interface MemberEntry {
  user_id: number;
  username: string;
  display_name?: string;
  avatar_url?: string;
  status: 1 | 2 | 3 | 4 | 5;
  score?: number | null;
}

interface StatusCounts {
  s1: number; s2: number; s3: number; s4: number; s5: number; total: number;
}

const PAGE_SIZE = 20;

// ─── アクティビティカード ───────────────────────────────
const ActivityCard: React.FC<{
  activity: ActivityEntry;
  statusConfig: ReturnType<typeof useStatusConfig>;
  onReactionUpdate: (id: number, r: Record<number, number>, ur: number | null) => void;
}> = ({ activity, statusConfig, onReactionUpdate }) => {
  const navigate = useNavigate();
  const actionText = getActionText(activity.action_type, activity.old_status, activity.new_status);
  if (actionText === null) return null;
  const conf = activity.new_status ? statusConfig[activity.new_status as keyof typeof statusConfig] : null;

  return (
    <Box sx={{
      px: { xs: 1.5, md: 2 }, py: { xs: 1.25, md: 1.75 },
      backgroundColor: 'rgba(255,255,255,0.03)',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <Box sx={{ display: 'flex', gap: { xs: 1.25, md: 1.75 } }}>
        <Avatar
          src={activity.user.avatar_url}
          sx={{ width: { xs: 36, md: 40 }, height: { xs: 36, md: 40 }, flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
          onClick={() => navigate(`/profile/${activity.user.username}`)}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="body2" sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate(`/profile/${activity.user.username}`)}
            >
              {activity.user.display_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }}>
              {formatActivityTime(activity.created_at)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {conf && <conf.Icon sx={{ fontSize: '0.85rem', color: conf.color }} />}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{actionText}</Typography>
            {activity.score != null && (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>·</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>★ {activity.score}</Typography>
              </>
            )}
          </Box>
          {activity.review_text && (
            <Box sx={{
              mt: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 1,
              color: 'text.secondary', fontSize: { xs: '0.7rem', md: '0.75rem' }, lineHeight: 1.4,
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            }}>
              {formatReviewText(activity.review_text, true)}
            </Box>
          )}
          <ActivityReactions
            targetId={activity.id} reactions={activity.reactions} userReaction={activity.user_reaction}
            isPublic={true} onReactionUpdate={(id, r, ur) => onReactionUpdate(id, r, ur)}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ─── スケルトン ─────────────────────────────────────────
const CardSkeleton: React.FC = () => (
  <Box sx={{ px: 2, py: 1.75, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
    <Box sx={{ display: 'flex', gap: 1.75 }}>
      <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0, bgcolor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Skeleton variant="text" width={50}  sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
        </Box>
        <Skeleton variant="text" width={160} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
      </Box>
    </Box>
  </Box>
);


// ─── メインページ ────────────────────────────────────────
const GameCommunityPage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusConfig = useStatusConfig();
  const queryClient = useQueryClient();

  const tab = (searchParams.get('tab') || 'activity') as TabType;
  const activeStatus = parseInt(searchParams.get('status') || '0', 10);
  const page = parseInt(searchParams.get('page') || '1', 10);
  const gid = gameId ? parseInt(gameId, 10) : 0;

  const { data: game, isLoading: gameLoading } = useQuery({
    queryKey: ['game', gid],
    queryFn: () => getGameDetails(gid),
    enabled: !!gid,
    staleTime: 60_000,
  });

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['game-activities', gid, activeStatus, page],
    queryFn: () => getGameActivities(gid, activeStatus || undefined, page, PAGE_SIZE),
    enabled: !!gid && tab === 'activity',
    staleTime: 30_000,
  });

  const { data: membersData, isLoading: memberLoading } = useQuery({
    queryKey: ['game-members', gid, activeStatus, page],
    queryFn: () => getGameUsers(gid, activeStatus || undefined, page, PAGE_SIZE),
    enabled: !!gid && tab === 'members',
    staleTime: 30_000,
  });

  const activities: ActivityEntry[] = activityData?.results ?? [];
  const activityCounts: StatusCounts | null = activityData?.status_counts ?? null;
  const activityPages = activityData?.total_pages ?? 1;

  const members: MemberEntry[] = membersData?.users ?? [];
  const memberPages = membersData?.total_pages ?? 1;
  const memberCounts: StatusCounts | null = membersData?.status_counts ?? (game?.status_counts ? {
    s1: game.status_counts.s1 ?? 0,
    s2: game.status_counts.s2 ?? 0,
    s3: game.status_counts.s3 ?? 0,
    s4: game.status_counts.s4 ?? 0,
    s5: game.status_counts.s5 ?? 0,
    total: (game.status_counts.s1 ?? 0) + (game.status_counts.s2 ?? 0) + (game.status_counts.s3 ?? 0) + (game.status_counts.s4 ?? 0) + (game.status_counts.s5 ?? 0),
  } : null);

  // ページネーション変更時だけトップへ戻す
  const prevPageRef = useRef(page);
  useEffect(() => {
    if (prevPageRef.current !== page) {
      window.scrollTo(0, 0);
      prevPageRef.current = page;
    }
  }, [page]);

  const handleTabChange = (newTab: TabType) => {
    setSearchParams({ tab: newTab });
  };

  const handleStatusFilter = (statusNum: number) => {
    const next = statusNum !== 0 && statusNum === activeStatus ? 0 : statusNum;
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (next !== 0) params.set('status', String(next));
    setSearchParams(params);
  };

  const handleReactionUpdate = (id: number, reactions: Record<number, number>, userReaction: number | null) => {
    queryClient.setQueryData(
      ['game-activities', gid, activeStatus, page],
      (old: any) => old ? {
        ...old,
        results: (old.results ?? []).map((a: ActivityEntry) =>
          a.id === id ? { ...a, reactions, user_reaction: userReaction } : a
        ),
      } : old
    );
  };

  const paginationUrl = (p: number) => {
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (activeStatus) params.set('status', String(activeStatus));
    if (p > 1) params.set('page', String(p));
    return `/game/${gameId}/community?${params.toString()}`;
  };

  const gameName = game?.japanese_name || game?.name;


  return (
    <>
      <Helmet>
        <title>{gameName ? `コミュニティ: ${gameName} | ${SITE_FULL_NAME}` : `コミュニティ | ${SITE_FULL_NAME}`}</title>
      </Helmet>

      <Box sx={{ mt: 2, mb: 4 }}>
        {/* ゲームヘッダー */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
          <Box
            component={Link} to={`/game/${gameId}`}
            sx={{ flexShrink: 0, textDecoration: 'none', '&:hover': { opacity: 0.8 }, transition: 'opacity 0.15s' }}
          >
            {gameLoading
              ? <Skeleton variant="rectangular" width={56} height={74} sx={{ borderRadius: 0.75, bgcolor: 'rgba(255,255,255,0.08)' }} />
              : game?.cover_hd
                ? <Box component="img" src={game.cover_hd} alt={game?.name} sx={{ width: 56, height: 74, objectFit: 'cover', borderRadius: 0.75, display: 'block' }} />
                : <Box sx={{ width: 56, height: 74, borderRadius: 0.75, bgcolor: 'rgba(255,255,255,0.06)' }} />
            }
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              コミュニティ
            </Typography>
            {gameLoading
              ? <Skeleton variant="text" width={180} height={26} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
              : <Typography
                  component={Link} to={`/game/${gameId}`}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 700, lineHeight: 1.3, textDecoration: 'none', color: 'inherit', '&:hover': { opacity: 0.8 } }}
                >
                  {gameName}
                </Typography>
            }

            {gameLoading
              ? <Skeleton variant="text" width={60} height={20} sx={{ mt: 0.75, bgcolor: 'rgba(255,255,255,0.08)' }} />
              : memberCounts && memberCounts.total > 0
                ? (
                  <Box sx={{ mt: 0.75, display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.07)' }}>
                    <PeopleAltOutlinedIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                      {memberCounts.total.toLocaleString()}
                    </Typography>
                  </Box>
                )
                : null
            }
          </Box>
        </Box>

        {/* タブ切り替え */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {(['activity', 'members'] as const).map(t => (
            <Box
              key={t}
              onClick={() => handleTabChange(t)}
              sx={{
                pb: 1.25,
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: tab === t ? 600 : 400,
                color: tab === t ? 'text.primary' : 'text.secondary',
                borderBottom: tab === t ? '2px solid white' : '2px solid transparent',
                mb: '-1px',
                transition: 'color 0.15s',
                '&:hover': { color: 'text.primary' },
                userSelect: 'none',
              }}
            >
              {t === 'activity' ? 'アクティビティ' : 'メンバー'}
            </Box>
          ))}
        </Box>

        <Box sx={{ maxWidth: 720 }}>

            {/* ─── アクティビティタブ ─── */}
            {tab === 'activity' && (
              <>
                {activityCounts && (
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 2 }}>
                    {activityCounts.total.toLocaleString()}件のアクティビティ
                  </Typography>
                )}

                {activityLoading ? (
                  <Stack spacing={1}>{[...Array(6)].map((_, i) => <CardSkeleton key={i} />)}</Stack>
                ) : activities.length === 0 ? (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary' }}>アクティビティがありません</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {activities.map(a => (
                      <ActivityCard key={a.id} activity={a} statusConfig={statusConfig} onReactionUpdate={handleReactionUpdate} />
                    ))}
                  </Stack>
                )}
                {activityPages > 1 && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination count={activityPages} page={page} renderItem={(item) => (
                      <PaginationItem component={Link} to={paginationUrl(item.page ?? 1)} {...item} />
                    )} />
                  </Box>
                )}
              </>
            )}

            {/* ─── メンバータブ ─── */}
            {tab === 'members' && (
              <>
                <Stack direction="row" sx={{ mb: 2, flexWrap: 'wrap', gap: '6px' }}>
                  {([1, 2, 3, 4, 5] as const).map(s => {
                    const conf = statusConfig[s];
                    const count = memberCounts?.[`s${s}` as keyof StatusCounts] ?? 0;
                    if (!count && activeStatus !== s) return null;
                    const isActive = activeStatus === s;
                    return (
                      <Chip
                        key={s}
                        icon={<conf.Icon sx={{ fontSize: '0.85rem !important', color: `${conf.color} !important` }} />}
                        label={`${conf.label} ${count.toLocaleString()}`}
                        size="small"
                        onClick={() => handleStatusFilter(s)}
                        variant={isActive ? 'filled' : 'outlined'}
                        sx={{
                          fontSize: '0.75rem',
                          ...(isActive
                            ? { backgroundColor: `${conf.color}22`, borderColor: conf.color, color: conf.color }
                            : { borderColor: `${conf.color}66`, color: `${conf.color}cc` }),
                        }}
                      />
                    );
                  })}
                </Stack>
                {memberLoading ? (
                  <List disablePadding>
                    {[...Array(8)].map((_, i) => (
                      <ListItemButton key={i} disabled sx={{ py: 1.5 }}>
                        <ListItemAvatar><Skeleton variant="circular" width={44} height={44} /></ListItemAvatar>
                        <ListItemText primary={<Skeleton width={120} />} secondary={<Skeleton width={80} />} />
                        <Skeleton variant="text" width={60} />
                      </ListItemButton>
                    ))}
                  </List>
                ) : members.length === 0 ? (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary' }}>メンバーが見つかりません</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {members.map(member => {
                      const conf = statusConfig[member.status];
                      return (
                        <ListItemButton
                          key={member.user_id}
                          component={Link}
                          to={`/profile/${member.username}`}
                          sx={{ borderRadius: 1, mb: 0.25, '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                        >
                          <ListItemAvatar>
                            <Box sx={{ position: 'relative', width: 44, height: 44, mr: 0.5 }}>
                              <Avatar src={member.avatar_url} alt={member.display_name || member.username} sx={{ width: 44, height: 44 }}>
                                {!member.avatar_url && <PersonIcon />}
                              </Avatar>
                              <Box sx={{ position: 'absolute', bottom: 1, right: 1, width: 12, height: 12, borderRadius: '50%', backgroundColor: conf.color, border: '2px solid #121212' }} />
                            </Box>
                          </ListItemAvatar>
                          <ListItemText
                            primary={member.display_name || member.username}
                            secondary={`@${member.username}`}
                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            {member.score != null && (
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>★ {member.score}</Typography>
                            )}
                            <Typography variant="caption" sx={{ color: conf.color, display: { xs: 'none', sm: 'block' } }}>
                              {conf.label}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
                {memberPages > 1 && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination count={memberPages} page={page} renderItem={(item) => (
                      <PaginationItem component={Link} to={paginationUrl(item.page ?? 1)} {...item} />
                    )} />
                  </Box>
                )}
              </>
            )}

        </Box>
      </Box>
    </>
  );
};

export default GameCommunityPage;
